import React from "react";
import { Alert, Box, Button, Chip, CircularProgress, Divider, Paper, Stack, TextField, Typography } from "@mui/material";
import { contentTemplateLibraryService, type ContentBlueprint, type ContentFormatDefinition, type ContentLibrarySnapshot } from "../../services/contentTemplateLibraryService";
import { ApiError } from "../../lib/api";

type Entry = { key: string; kind: "format"; value: ContentFormatDefinition } | { key: string; kind: "blueprint"; value: ContentBlueprint };
function selectedFromUrl() { return new URLSearchParams(window.location.search).get("definition"); }
function readable(value: unknown): string {
  if (typeof value === "string") return value;
  return JSON.stringify(value) ?? "Not specified";
}
function TextValue({ children }: { children: React.ReactNode }) {
  return <Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{children}</Typography>;
}
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return <Box><Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{title}</Typography>{children}</Box>;
}
function FormatDetail({ value }: { value: ContentFormatDefinition }) {
  const beats = value.script_structure.beats;
  return <Stack spacing={2}>
    <TextValue>{value.description || "No description recorded."}</TextValue>
    <Typography variant="body2">Catalog slug: {value.slug} · {value.pillar} · {value.status}</Typography>
    <Section title="Output channels"><TextValue>{value.output_channels.join(" · ") || "None recorded"}</TextValue></Section>
    <Section title="Required inputs"><Box component="ul" sx={{ pl: 3, my: 0 }}>{value.required_inputs.map((item, i) => <li key={i}>{item}</li>)}</Box></Section>
    <Section title="Ordered story beats">
      {Array.isArray(beats) && beats.length ? <Box component="ol" sx={{ pl: 3, my: 0 }}>{beats.map((beat, i) => <li key={i}><TextValue>{readable(beat)}</TextValue></li>)}</Box> : <Typography>No ordered beats recorded.</Typography>}
      {Object.entries(value.script_structure).filter(([key]) => key !== "beats").map(([key, data]) => <Typography key={key} variant="body2" sx={{ mt: 1 }}>{key.replace(/_/g, " ")}: {readable(data)}</Typography>)}
    </Section>
    <Section title="Asset requirements">{Object.keys(value.required_asset_tags).length ? Object.entries(value.required_asset_tags).map(([key, data]) => <Typography key={key}>{key}: {Array.isArray(data) ? data.map(readable).join(", ") : readable(data)}</Typography>) : <Typography>No asset requirements recorded.</Typography>}</Section>
    <Section title="CTA pattern"><TextValue>{value.cta_pattern || "No CTA pattern recorded."}</TextValue></Section>
    <Typography variant="caption">Source: Workbench content_formats · Updated {value.updated_at}</Typography>
  </Stack>;
}
function BlueprintDetail({ value }: { value: ContentBlueprint }) {
  return <Stack spacing={2}>
    <TextValue>{value.intent}</TextValue>
    <Typography variant="body2">Assembler slug: {value.format_slug} · Target maximum {value.max_total_sec} seconds</Typography>
    <Alert severity="info">These are recipe text patterns. Braced values are filled from research and the compiled plan; they are not the final text of a saved reel.</Alert>
    <Section title="Opening text pattern"><TextValue>{value.hook_caption}</TextValue></Section>
    <Section title="Ordered shot recipe">
      <Box component="ol" sx={{ pl: 3, my: 0 }}>{value.shots.map((shot, index) => <Box component="li" key={index} sx={{ mb: 2 }}>
        <Typography fontWeight={700}>{shot.role.replace(/_/g, " ")} · {shot.min}–{shot.max} clips · {shot.duration_sec}s target per clip</Typography>
        <Typography variant="body2">{shot.min === 0 ? "Optional" : "Required"} asset role · Audio: {shot.audio} · Camera movement: {shot.ken_burns} · Transition: {shot.transition_after}</Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>Caption templates (source order):</Typography>
        {shot.caption_templates.map((caption, i) => <TextValue key={i}>{caption}</TextValue>)}
      </Box>)}</Box>
    </Section>
    <Section title="Closing text pattern"><TextValue>{value.cta_caption}</TextValue></Section>
    <Section title="Music direction"><TextValue>{value.music.mood}</TextValue><TextValue>{value.music.genre}</TextValue></Section>
    <Typography variant="caption">Source: Workbench assembler BLUEPRINTS</Typography>
  </Stack>;
}

export default function ContentTemplateLibrary() {
  const [snapshot, setSnapshot] = React.useState<ContentLibrarySnapshot | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [failure, setFailure] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState("");
  const [selected, setSelected] = React.useState<string | null>(selectedFromUrl);
  const epoch = React.useRef(0);
  const loadingRef = React.useRef(false);
  const load = React.useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    const request = ++epoch.current;
    setLoading(true);
    try {
      const result = await contentTemplateLibraryService.list();
      if (request !== epoch.current) return;
      setSnapshot(result); setFailure(null);
    } catch (error) {
      if (request !== epoch.current) return;
      // Session loss revokes access to the last read instead of keeping it visible.
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) setSnapshot(null);
      setFailure(error instanceof ApiError && (error.status === 401 || error.status === 403)
        ? "Sign in with a production admin account to view the template library."
        : error instanceof ApiError && (error.body as { detail?: string } | null)?.detail === "Content template library is disabled"
          ? "The template library connection is not enabled yet."
          : "The template library could not be refreshed. Try again.");
    } finally {
      if (request === epoch.current) { setLoading(false); loadingRef.current = false; }
    }
  }, []);
  React.useEffect(() => {
    loadingRef.current = false;
    void load();
    return () => { epoch.current++; loadingRef.current = false; };
  }, [load]);
  React.useEffect(() => {
    const sync = () => setSelected(selectedFromUrl());
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);
  const choose = (key: string | null) => {
    const url = new URL(window.location.href);
    if (key) url.searchParams.set("definition", key); else url.searchParams.delete("definition");
    if (url.href !== window.location.href) window.history.pushState(window.history.state, "", url);
    setSelected(key);
  };
  const entries: Entry[] = snapshot ? [
    ...snapshot.formats.map(value => ({ key: `format:${value.id}`, kind: "format" as const, value })),
    ...snapshot.blueprints.map(value => ({ key: `blueprint:${value.format_slug}`, kind: "blueprint" as const, value })),
  ] : [];
  const visible = entries.filter(entry => `${entry.value.name} ${entry.kind === "format" ? entry.value.slug + " " + entry.value.pillar : entry.value.format_slug}`.toLowerCase().includes(query.trim().toLowerCase()));
  const detail = entries.find(entry => entry.key === selected);
  return <Stack spacing={2}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
      <Typography variant="h5">Template & recipe library</Typography>
      <Button onClick={() => void load()} disabled={loading}>Refresh library</Button>
    </Stack>
    <Typography>Browse the stored content formats and the assembler’s shot recipes. Formats describe the story and inputs; recipes describe the sequence, text patterns, footage and timing.</Typography>
    <Alert severity="info">Definitions are read-only here. To edit exact text on an eligible saved reel, open Videos, review the reel and select a beat. Creating or saving a new recipe and rendering from a prompt need a connected draft-creation workflow.</Alert>
    {failure && <Alert severity={snapshot ? "warning" : "error"}>{snapshot && "Showing the last successful read. "}{failure}</Alert>}
    {loading && <Stack direction="row" spacing={1} alignItems="center" role="status"><CircularProgress size={20} /><Typography>Loading source definitions…</Typography></Stack>}
    {snapshot && <>
      <Typography variant="body2">{snapshot.formats.length} catalog formats · {snapshot.blueprints.length} assembler recipes · Last read {snapshot.fetched_at}</Typography>
      {snapshot.formats_state === "unavailable" && <Alert severity="warning">The stored format catalog is unavailable. Assembler recipes are available; this is not a complete catalog read.</Alert>}
      {snapshot.formats_state === "ready" && !snapshot.formats_complete && <Alert severity="warning">The catalog reached its {snapshot.format_limit}-format read limit. More formats may exist; this list is not complete.</Alert>}
      {snapshot.formats_state === "ready" && snapshot.formats.length === 0 && <Alert severity="info">No stored formats were returned by the catalog.</Alert>}
      {entries.length === 0 && <Typography>No content definitions were returned.</Typography>}
      <TextField label="Search definitions" value={query} onChange={event => setQuery(event.target.value)} fullWidth />
      {entries.length > 0 && visible.length === 0 && <Typography>No definitions match your search.</Typography>}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(230px, 1fr) 2fr" }, gap: 2 }}>
        <Stack spacing={1} aria-label="Content definitions">
          {visible.map(entry => <Button key={entry.key} variant={entry.key === selected ? "contained" : "outlined"} onClick={() => choose(entry.key)} aria-pressed={entry.key === selected} sx={{ justifyContent: "space-between", textAlign: "left", gap: 1, minHeight: 48 }}>
            {entry.value.name}<Chip size="small" label={entry.kind === "format" ? "Format" : "Recipe"} />
          </Button>)}
        </Stack>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
          {detail ? <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" gap={1}><Typography variant="h6">{detail.value.name}</Typography><Button onClick={() => choose(null)}>Close details</Button></Stack>
            <Divider />
            {detail.kind === "format" ? <FormatDetail value={detail.value} /> : <BlueprintDetail value={detail.value} />}
          </Stack> : <Typography>{selected ? "This definition is not in the current read. Choose another definition or refresh." : "Choose a format or recipe to inspect its ordered beats and requirements."}</Typography>}
        </Paper>
      </Box>
    </>}
  </Stack>;
}
